/**
 * 画面风格预设（art_style）—— 全链路唯一真源。
 *
 * 🔴 2026-10-11（老板给定 21 条风格表）：画面风格菜单从旧的 9 条泛化档位
 * （真人写实/日系动漫/…）换成这份**题材化**的风格表：
 *   - `id`     = 存库值（projects.art_style 直接存这个 slug）
 *   - `name`   = 下拉展示名
 *   - `prompt` = 中文提示词片段（老板给定文本，逐字使用，不得改写）
 *   - `medium` = 介质大类（photo 实拍 / animation 动画）—— 决定英文介质锚句，
 *                因为两者在视频模型里是互斥的：给动画风格挂
 *                「real skin texture / real optical lens」这类实拍锚会把画面拉回写实。
 *
 * 注入点（三处，缺一不可，全部由本模块单点供料）：
 *   1. 视频提示词风格锚 `[Style | Fixed Anchor]`（generate/route.js buildStyleAnchorLine）
 *   2. 场景 / 道具出图提示词的 `styleLabel`（getProjectVisualSettings 合成）
 *   3. 角色立绘 / 四视图 prompt 的 `PRIMARY MEDIUM`（prompts/character-image.ts）
 *
 * 旧 id（realistic / anime / …）**保留可解析**：已建项目的 art_style 不动、
 * 风格锚与角色介质指令按老口径继续生效，只是不再出现在选择菜单里。
 */

/** 实拍介质锚句（英文；与旧「真人写实」锚句逐字一致，保证存量项目出图口径不变） */
export const EN_PHOTO_MEDIUM =
  "live-action cinematic photography, real skin texture and visible pores, natural light ratio with soft shadows, film grain with slight noise, real optical lens imaging (true depth of field, natural motion blur).";

export type VisualStyleMedium = "photo" | "animation";

export interface VisualStylePreset {
  /** 存库值（projects.art_style） */
  id: string;
  /** 中文展示名（下拉文案） */
  name: string;
  /** 中文提示词片段（老板给定，逐字注入） */
  prompt: string;
  /** 介质大类 */
  medium: VisualStyleMedium;
  /** 英文介质锚句（视频/角色 prompt 用；动画风格必须显式排除实拍质感） */
  mediumEn: string;
}

/** 动画介质通用锚句（照片系之外的兜底，防止被实拍锚污染） */
export const EN_ANIMATION_MEDIUM =
  "stylized non-photorealistic rendering, hand-crafted illustration/animation texture, no live-action camera optics, no real-people skin pores.";

export const VISUAL_STYLE_PRESETS: VisualStylePreset[] = [
  {
    id: "retro-sci-fi-atompunk",
    name: "复古科幻原子朋克",
    prompt:
      "复古科幻原子朋克风格，1950 年代未来主义美学，流线型金属与原子能符号，霓虹高光，金属质感，高对比色彩，轻微胶片颗粒",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "palace-intrigue-cold",
    name: "宫斗权谋冷峻",
    prompt:
      "中国宫廷权谋题材冷峻风格，低饱和暗调，克制光影，庄重构图，华贵但压抑的宫廷氛围，硬朗轮廓，戏剧化侧光",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "domestic-suspense-cold",
    name: "国产悬疑冷调",
    prompt: "国产悬疑影视冷调风格，偏青灰色调，低调光，阴影浓重，写实摄影质感，紧张压抑氛围，细节丰富",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "ancient-romance-soft",
    name: "古偶唯美柔光",
    prompt: "中国古代偶像剧唯美柔光风格，梦幻柔焦，暖色薄纱光晕，精致古装妆造，背景虚化，浪漫飘逸氛围",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "ancient-chinese-mythology",
    name: "中国古代神话史诗",
    prompt:
      "中国古代神话史诗风格，上古洪荒气质，苍茫山河与云雾神光，青铜礼器与粗纻麻衣质感，水墨青绿与矿物颜料色调，庄严神圣，史诗大场面，电影级光影，忌现代偶像剧柔光与甜宠滤镜",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "japanese-youth-film",
    name: "日式青春胶片",
    prompt: "日式青春题材胶片摄影风格，柯达胶片色调，自然阳光，浅景深，细腻颗粒，青涩真挚的日常氛围",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "japanese-daily-natural",
    name: "日式生活自然",
    prompt: "日式生活纪录片自然光影风格，柔和自然光，低对比，真实日常场景，安静治愈，轻微胶片质感",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "korean-urban-soft",
    name: "韩剧都市柔光",
    prompt: "韩剧都市题材柔光风格，暖色滤镜，通透肤质，都市背景虚化，浪漫温柔灯光氛围",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "chinese-urban-realistic",
    name: "国产都市写实",
    prompt: "国产都市现实题材写实摄影风格，自然光，真实生活场景，中性色调，细节锐利，无过度美化",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "wuxia-realistic-photo",
    name: "武侠江湖写实摄影",
    prompt: "中国武侠江湖题材写实摄影风格，自然光影，真实地形与服饰质感，动态构图，江湖氛围，电影级景深",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "90s-realistic-film",
    name: "90 年代写实电影",
    prompt: "1990 年代写实电影风格，胶片质感，自然肤色，时代感服装与环境，柔和对比，怀旧色调",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "retro-narrative-film",
    name: "复古叙事电影",
    prompt: "复古叙事电影风格，经典电影构图，胶片色彩分级，富有故事感的场景调度，电影级布光",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "american-retro-hollywood",
    name: "美式复古好莱坞",
    prompt: "美式复古好莱坞黄金年代风格，高对比布光，暖调彩色或经典黑白，明星质感，华丽景深",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "neon-cyberpunk-film",
    name: "霓虹赛博电影",
    prompt: "霓虹赛博朋克电影风格，蓝紫霓虹灯光，雨夜反射，高对比，未来都市，烟雾与全息感光效",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "90s-rural-china-film",
    name: "90 年代中国农村电影",
    prompt: "1990 年代中国农村题材电影风格，自然光，土黄与绿色调，粗糙真实质感，乡土生活氛围",
    medium: "photo",
    mediumEn: EN_PHOTO_MEDIUM,
  },
  {
    id: "cgi-3d-animation",
    name: "3D 动画",
    prompt:
      "电影级三维 CGI 动画风格，皮克斯 / 梦工厂气质，圆润造型与清晰轮廓，柔和体积光与次表面散射，干净材质与饱和配色，浅景深，非写实摄影、非日系赛璐璐平面、非剪纸扁平",
    medium: "animation",
    mediumEn:
      "cinematic 3D CGI animation, Pixar/DreamWorks-grade rounded forms with clean silhouettes, soft volumetric light and subsurface scattering, clean materials and saturated palette, shallow depth of field; non-photorealistic, not 2D cel, not flat cut-out.",
  },
  {
    id: "ghibli-handdrawn-anime",
    name: "宫崎骏气质手绘",
    prompt:
      "手绘二维动画电影气质，水彩与水粉背景，柔和自然光与金色黄昏，写实人体比例与朴素五官（非大眼睛赛璐璐美少女），生活化服饰，茂盛草木、风吹草地与流动云层，温暖土黄与青绿，空气透视与电影构图，非写实摄影、非 3D CGI、非像素风、非剪纸扁平",
    medium: "animation",
    mediumEn:
      "hand-drawn 2D animation film look, watercolor and gouache backgrounds, soft natural light with golden dusk, realistic human proportions and plain facial features (not big-eyed cel anime), everyday clothing, lush vegetation, wind-blown grass and drifting clouds, warm earth-yellow and blue-green palette, atmospheric perspective and cinematic composition; non-photorealistic, not 3D CGI, not pixel art, not flat cut-out.",
  },
  {
    id: "tezuka-era-cartoon",
    name: "手冢治虫时代卡通画风",
    prompt: "手冢治虫时代经典日式卡通画风，简洁线条，复古动画平涂着色，怀旧动画质感",
    medium: "animation",
    mediumEn: `classic Tezuka-era Japanese cartoon look, simple clean linework, retro flat animation coloring, nostalgic vintage animation texture; ${EN_ANIMATION_MEDIUM}`,
  },
  {
    id: "shanghai-animation",
    name: "上美画风",
    prompt: "上海美术电影制片厂经典动画画风，中国民族绘画韵味，水彩与工笔结合，诗意唯美，传统色彩",
    medium: "animation",
    mediumEn: `Shanghai Animation Film Studio classic look, Chinese folk-painting charm, watercolor combined with gongbi fine linework, poetic and refined, traditional palette; ${EN_ANIMATION_MEDIUM}`,
  },
  {
    id: "pixel-art",
    name: "像素风",
    prompt: "像素艺术风格，清晰像素块，复古游戏美学，有限色板，8-bit 或 16-bit 质感",
    medium: "animation",
    mediumEn: `pixel art look, crisp visible pixel blocks, retro game aesthetic, limited palette, 8-bit/16-bit rendering; ${EN_ANIMATION_MEDIUM}`,
  },
  {
    id: "shadow-puppet-illustration",
    name: "皮影戏插画",
    prompt: "中国皮影戏插画画风，剪影轮廓，镂空纹理，暖色背光，民间艺术装饰性，层叠投影效果",
    medium: "animation",
    mediumEn: `Chinese shadow-puppet illustration style, silhouette contours, pierced openwork textures, warm backlight, folk-art decorative patterning, layered shadow projection; ${EN_ANIMATION_MEDIUM}`,
  },
];

const PRESET_BY_ID = new Map(VISUAL_STYLE_PRESETS.map((p) => [p.id, p]));

/**
 * 旧版档位 → 英文介质锚句。只为**存量项目**保留解析（出图口径与旧版完全一致），
 * 不出现在选择菜单里。
 */
const LEGACY_MEDIUM_EN: Record<string, string> = {
  realistic: EN_PHOTO_MEDIUM,
};

export function findStylePreset(id?: string | null): VisualStylePreset | undefined {
  const v = String(id ?? "").trim();
  return v ? PRESET_BY_ID.get(v) : undefined;
}

/** UI 下拉选项（仅新风格表；旧 id 不展示） */
export const STYLE_OPTIONS: ReadonlyArray<{ id: string; name: string }> = VISUAL_STYLE_PRESETS.map(
  (p) => ({ id: p.id, name: p.name })
);

/** 中文提示词片段；未注册（含旧 id / 自由文本）返回空串 */
export function resolveStylePromptFragment(id?: string | null): string {
  return findStylePreset(id)?.prompt ?? "";
}

/** 英文介质锚句：新风格表按 preset；旧 id 只给「真人写实」补实拍锚（与旧口径一致） */
export function resolveStyleMediumEn(id?: string | null): string {
  const v = String(id ?? "").trim();
  return findStylePreset(v)?.mediumEn ?? LEGACY_MEDIUM_EN[v] ?? "";
}

/**
 * 注入提示词的风格块：`名称；中文片段；英文介质锚`。
 * 旧 id / 自由文本 → 只回中文标签本身（等同旧行为）。
 */
export function resolveStyleDirective(id?: string | null): string {
  const preset = findStylePreset(id);
  if (preset) return `${preset.name}；${preset.prompt}`;
  const raw = String(id ?? "").trim();
  return raw;
}

// ─────────────────────────────────────────────────────────────────────
// 道具侧风格收敛（2026-10-26）
// ─────────────────────────────────────────────────────────────────────

/**
 * 与「**单件道具 + 纯白底 + 中性棚拍光**」**直接冲突**的风格片段。
 *
 * ══════════════════════════════════════════════════════════════════════
 * 为什么需要（真机 prompt 取证）
 * ══════════════════════════════════════════════════════════════════════
 * `artStyleLabel` 是**场景/人物向**的风格通道，本项目实际值是：
 *
 *   `韩剧都市柔光；韩剧都市题材柔光风格，暖色滤镜，通透肤质，都市背景虚化，
 *    浪漫温柔灯光氛围；色彩基调：暖橙怀旧；时代美学：当代都市；氛围情绪：温情感伤`
 *
 * 而道具图的规范正文写着「**纯白底、中性棚拍光、不做氛围光/彩色光/景深虚化**」。
 * 两段话正面打架：
 *
 *   · `都市背景虚化` + `浪漫温柔灯光氛围` ←→ `纯白底` + `中性棚拍光`
 *   · `暖色滤镜`                        ←→ `不做彩色光`
 *   · `通透肤质`                        ←→ 道具没有皮肤（纯噪声，还会把模型往人像上带）
 *   · `氛围情绪：温情感伤`              ←→ 道具图是资产参考图，不带叙事情绪
 *
 * 模型只能折衷 —— 实测出来就是「白底上打着暖橙氛围光、背景还有虚化」的四不像。
 *
 * ── 处理原则：只删**与纯白棚拍直接冲突**的片段，保留项目视觉身份 ──────────
 *
 *   ✅ 保留：介质/风格名（韩剧都市柔光、国产都市写实）、主色与冷暖（暖橙怀旧）、
 *           年代美学（影响工艺与形制）
 *   ❌ 删除：背景虚化/景深、灯光氛围、滤镜、肤质、氛围情绪、参考导演手法
 *
 * ⚠️ **按片段删，不按整段删**：`韩剧都市题材柔光风格，暖色滤镜，通透肤质，都市背景虚化，
 *    浪漫温柔灯光氛围` 是**一个** clause，整段删会把「韩剧都市」这个项目身份一起丢掉。
 */
const PROP_STYLE_DROP_FRAGMENTS: readonly string[] = [
  // 背景与景深（与「纯白底」冲突）
  "都市背景虚化",
  "背景虚化",
  "虚化",
  "景深",
  // 灯光与氛围（与「中性棚拍光、不做氛围光」冲突）
  "浪漫温柔灯光氛围",
  "温柔灯光氛围",
  "灯光氛围",
  "氛围光",
  // 滤镜（与「不做彩色光」冲突）
  "暖色滤镜",
  "冷色滤镜",
  "滤镜",
  // 皮肤（道具没有皮肤，纯噪声）
  "通透肤质",
  "肤质",
];

/** 整段丢弃的 clause：叙事/运镜类，对一张资产参考图没有视觉价值。 */
const PROP_STYLE_DROP_CLAUSE = /^(氛围情绪|参考导演手法)\s*[:：]/;

/**
 * 把 `artStyleLabel` 收敛成**道具图能用的那一份**。
 *
 * 纯函数，可单测。空输入返回空串（调用方据此跳过风格块）。
 *
 * 例：
 *   `韩剧都市柔光；韩剧都市题材柔光风格，暖色滤镜，通透肤质，都市背景虚化，
 *    浪漫温柔灯光氛围；色彩基调：暖橙怀旧；时代美学：当代都市；氛围情绪：温情感伤`
 *   →
 *   `韩剧都市柔光；韩剧都市题材柔光风格；色彩基调：暖橙怀旧；时代美学：当代都市`
 */
export function convergeStyleLabelForProp(label?: string | null): string {
  const src = String(label ?? "").trim();
  if (!src) return "";

  const clauses = src
    .split(/[；;]/)
    .map((c) => c.trim())
    .filter(Boolean);

  const kept: string[] = [];
  for (const clause of clauses) {
    if (PROP_STYLE_DROP_CLAUSE.test(clause)) continue;

    let out = clause;
    for (const frag of PROP_STYLE_DROP_FRAGMENTS) out = out.split(frag).join("");
    // 删片段会留下悬空标点（`风格，，；` / `风格，`）
    out = out
      .replace(/[，,、]{2,}/g, "，")
      .replace(/[，,、]+$/, "")
      .replace(/^[，,、]+/, "")
      .replace(/\s{2,}/g, " ")
      .trim();

    // 只剩标点 / 只剩两三个字的残渣就别留了
    if (out.replace(/[，,、：:\s]/g, "").length < 2) continue;
    kept.push(out);
  }
  return kept.join("；");
}
